package top.wcpe.allincore.dsl.compiler.parse

import top.wcpe.allincore.dsl.compiler.ast.DrawAst
import top.wcpe.allincore.dsl.compiler.ast.DrawInstructionAst
import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.lex.Token
import top.wcpe.allincore.dsl.compiler.lex.TokenType
import top.wcpe.allincore.dsl.compiler.source.SourcePosition

/**
 * 解析 canvas 内的 `draw { ... }` / `once { ... }` 块（M11.6）。
 *
 * 指令类型：
 *  - 图元调用：`circle(cx, cy, r, color)`、`rect(x, y, w, h, color)` 等；
 *    合法函数名白名单详见 [PRIMITIVE_NAMES]。
 *  - 局部变量：`val name = expr`（仅在当前块可见，允许被后续指令的 ExprAst 引用）。
 *  - for 循环：`for (i in 0 until N) { body }`，from/to 必须是编译期整数常量。
 *  - if/else：`if (cond) { then } [else { elseBranch }]`。
 *
 * 为保持与现有 ExpressionParser 兼容，表达式内部出现的 val 名、for 变量、内置变量
 * （t / now / width / height）均按"裸标识符"解析（allowBareIdent = true），在 codegen
 * 阶段由 DrawBlobEmitter 统一处理。
 */
internal class DrawBlockParser(private val p: Parser) {

    /** 解析 `<keyword> { ... }` 块并返回 [DrawAst]。 */
    fun parseBlock(keyword: TokenType): DrawAst {
        require(keyword == TokenType.KW_DRAW || keyword == TokenType.KW_ONCE) {
            "draw/once 块关键字 token 类型不合法: $keyword"
        }
        val kwTok = p.expect(keyword)
        p.expect(TokenType.LBRACE, "${kwTok.lexeme} 块缺少 '{'")
        p.skipNewlines()
        val instructions = parseInstructions(TokenType.RBRACE)
        val close = p.expect(TokenType.RBRACE, "${kwTok.lexeme} 块缺少 '}'")
        return DrawAst(instructions = instructions, position = p.completePosition(kwTok.position, close))
    }

    /** 在当前游标处解析连续的 draw 指令，直到遇到 [terminator]。 */
    private fun parseInstructions(terminator: TokenType): List<DrawInstructionAst> {
        val list = mutableListOf<DrawInstructionAst>()
        while (!p.check(terminator) && !p.check(TokenType.EOF)) {
            list += parseInstruction()
            if (!p.check(terminator)) {
                p.expect(TokenType.NEWLINE, "draw 指令后缺少换行")
                p.skipNewlines()
            }
        }
        return list
    }

    /** 解析单条指令；失败时向上抛 [ParseException]。 */
    private fun parseInstruction(): DrawInstructionAst {
        val head = p.current()
        return when (head.type) {
            TokenType.KW_VAL -> parseValAssign()
            TokenType.KW_FOR -> parseFor()
            TokenType.KW_IF -> parseIf()
            TokenType.IDENTIFIER -> parsePrimitiveCall(head)
            else -> p.fail("draw 指令必须以图元函数名 / val / for / if 开头，实际为 '${head.lexeme}'", head.position)
        }
    }

    /** 解析图元调用：`name(arg1, arg2, ...)`。 */
    private fun parsePrimitiveCall(nameTok: Token): DrawInstructionAst.Primitive {
        val name = nameTok.lexeme
        if (name !in PRIMITIVE_NAMES) {
            p.fail("未知图元函数 '$name'；合法集合=${PRIMITIVE_NAMES}", nameTok.position)
        }
        p.advance()
        p.expect(TokenType.LPAREN, "图元 '$name' 调用缺少 '('")
        val args = parseArgs()
        val close = p.expect(TokenType.RPAREN, "图元 '$name' 调用缺少 ')'")
        return DrawInstructionAst.Primitive(
            name = name,
            args = args,
            position = p.completePosition(nameTok.position, close),
        )
    }

    /** 解析 `val name = expr`。 */
    private fun parseValAssign(): DrawInstructionAst.ValAssign {
        val valTok = p.expect(TokenType.KW_VAL)
        val nameTok = p.expect(TokenType.IDENTIFIER, "val 后缺少变量名")
        p.expect(TokenType.EQUALS, "val '${nameTok.lexeme}' 后缺少 '='")
        val expr = parseDrawExpr()
        return DrawInstructionAst.ValAssign(
            name = nameTok.lexeme,
            value = expr,
            position = valTok.position.endingAt(expr.range.end),
        )
    }

    /** 解析 `for (name in <intLit> until <intLit>) { body }`。 */
    private fun parseFor(): DrawInstructionAst.For {
        val forTok = p.expect(TokenType.KW_FOR)
        p.expect(TokenType.LPAREN, "for 后缺少 '('")
        val nameTok = p.expect(TokenType.IDENTIFIER, "for 变量名缺失")
        p.expect(TokenType.KW_IN, "for 变量后缺少 'in'")
        val from = readForRangeBound("for 下界")
        val kindTok = p.current()
        if (kindTok.type != TokenType.KW_UNTIL) {
            p.fail("draw for 仅支持 'until' 半开区间", kindTok.position)
        }
        p.advance()
        val to = readForRangeBound("for 上界")
        p.expect(TokenType.RPAREN, "for 头部缺少 ')'")
        p.expect(TokenType.LBRACE, "for 体缺少 '{'")
        p.skipNewlines()
        val body = parseInstructions(TokenType.RBRACE)
        val close = p.expect(TokenType.RBRACE, "for 体缺少 '}'")
        if (to < from) {
            p.fail("for 上界 ($to) 不得小于下界 ($from)", forTok.position)
        }
        return DrawInstructionAst.For(
            varName = nameTok.lexeme,
            from = from,
            to = to,
            body = body,
            position = p.completePosition(forTok.position, close),
        )
    }

    /** 解析 for 区间端点：必须是编译期整数字面量（允许可选负号）。 */
    private fun readForRangeBound(hint: String): Int {
        val startPos: SourcePosition = p.current().position
        val value = p.consumeSignedDouble("$hint 必须是整数常量")
        if (value != value.toInt().toDouble()) {
            p.fail("$hint 必须是整数常量，实际为 $value", startPos)
        }
        return value.toInt()
    }

    /** 解析 `if (cond) { then } [else { elseBranch }]`。 */
    private fun parseIf(): DrawInstructionAst.IfElse {
        val ifTok = p.expect(TokenType.KW_IF)
        p.expect(TokenType.LPAREN, "if 后缺少 '('")
        val cond = parseDrawExpr()
        p.expect(TokenType.RPAREN, "if 条件缺少 ')'")
        p.expect(TokenType.LBRACE, "if 体缺少 '{'")
        p.skipNewlines()
        val thenBranch = parseInstructions(TokenType.RBRACE)
        p.expect(TokenType.RBRACE, "if 体缺少 '}'")
        val elseBranch = if (p.check(TokenType.KW_ELSE)) {
            p.advance()
            p.expect(TokenType.LBRACE, "else 体缺少 '{'")
            p.skipNewlines()
            val list = parseInstructions(TokenType.RBRACE)
            p.expect(TokenType.RBRACE, "else 体缺少 '}'")
            list
        } else {
            emptyList()
        }
        val position = p.completePosition(ifTok.position)
        return DrawInstructionAst.IfElse(
            condition = cond,
            thenBranch = thenBranch,
            elseBranch = elseBranch,
            position = position,
        )
    }

    /** 解析图元调用的参数列表（允许 0 参数）。 */
    private fun parseArgs(): List<ExprAst> {
        val args = mutableListOf<ExprAst>()
        if (p.check(TokenType.RPAREN)) return args
        args += parseDrawExpr()
        while (p.check(TokenType.COMMA)) {
            p.advance()
            p.skipNewlines()
            args += parseDrawExpr()
        }
        return args
    }

    /**
     * 解析 draw 块中的表达式：复用 [ExpressionParser] 并开启 [ExpressionParser.allowBareIdent]，
     * 使得 val / for 变量与内置变量（t / now / width / height）可以作为 [ExprAst.IdentRef] 出现。
     */
    private fun parseDrawExpr(): ExprAst =
        ExpressionParser(p, allowBareIdent = true).parse()

    companion object {
        /** draw 块内允许的图元函数名白名单（与 DrawBlob opcode 一一对应）。 */
        val PRIMITIVE_NAMES: Set<String> = setOf(
            "rect", "circle", "dot", "line",
            "arc", "ring", "roundRect",
            "text", "polygon", "path", "drawImage",
            // v4 B.3：贝塞尔曲线
            "bezier", "bezierCubic",
            // v4 B.4：复合路径
            "pathBuild",
            // v4 B.5：像素位图（小地图前置）
            "bitmap",
        )

        /**
         * v4 B.4：pathBuild 实参列表内允许出现的 PATH_NODE 节点构造器名。
         * <p>这些名字仅在 pathBuild 第 1 参数（ListLiteralExpr）的元素位置被识别为 PathCommand 节点；
         * 在 ExprAst 层它们就是普通 CallExpr，由 DrawBlobEmitter.buildPathBuild 编译期校验合法性。</p>
         * <p>arcTo 本期不实现（spec §4.5）。</p>
         */
        val PATH_NODE_NAMES: Set<String> = setOf(
            "moveTo", "lineTo", "quadTo", "cubicTo", "close",
        )
    }
}
